import React, { useState, useEffect } from 'react';
import { X, FileSpreadsheet, Download } from 'lucide-react';
import { API_URL } from '../../services/api';

const StockFournisseurDetaille = ({ fournisseur, onClose }) => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (fournisseur) {
      loadStockDetaille();
    }
  }, [fournisseur]);

  const loadStockDetaille = async () => {
    try {
      const response = await fetch(`${API_URL}/fournisseurs/${fournisseur.id}/stock-detaille`);
      const data = await response.json();
      setStock(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement stock détaillé:', error);
      setStock([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return parseFloat(num).toFixed(2);
  };

  const totalAchat = stock.reduce((sum, item) => sum + parseFloat(item.prix_total_achat || 0), 0);
  const totalPaye = stock.reduce((sum, item) => sum + parseFloat(item.paye || 0), 0);
  const totalSolde = stock.reduce((sum, item) => sum + parseFloat(item.solde_a_regler || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <FileSpreadsheet className="h-6 w-6" />
            <div>
              <h3 className="text-xl font-bold">Stock Détaillé - {fournisseur?.nom}</h3>
              <p className="text-sm opacity-90">Vue type Excel avec historique complet</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Statistiques */}
        <div className="bg-gray-50 px-6 py-3 border-b grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Total Achats</p>
            <p className="text-lg font-bold text-blue-600">{formatNumber(totalAchat)} DH</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Payé</p>
            <p className="text-lg font-bold text-green-600">{formatNumber(totalPaye)} DH</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Solde à Régler</p>
            <p className="text-lg font-bold text-red-600">{formatNumber(totalSolde)} DH</p>
          </div>
        </div>

        {/* Tableau */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : stock.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-gray-500">Aucun achat enregistré pour ce fournisseur</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase border-r">Qté</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase border-r">Poids (carats)</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Catégorie</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Qté</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Poids Achat</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Cours $</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Prix Achat CT</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Prix Total</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Payé</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Date Paiement</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Solde</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Certificat</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Utilisé</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Restant</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Vendu</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stock.map((item, index) => (
                  <tr key={item.id || index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(item.date)}</td>
                    <td className="px-3 py-2">{item.designation}</td>
                    <td className="px-3 py-2">{item.categorie || '-'}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(item.qte)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="font-bold text-blue-600">💎 {formatNumber(item.poids_achat)} ct</span>
                    </td>
                    <td className="px-3 py-2 text-right">{item.cours_dollar ? formatNumber(item.cours_dollar) : '-'}</td>
                    <td className="px-3 py-2 text-right">{item.prix_achat_ct ? formatNumber(item.prix_achat_ct) : formatNumber(item.prix_achat_unitaire)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatNumber(item.prix_total_achat)}</td>
                    <td className="px-3 py-2 text-right text-green-600">{formatNumber(item.paye)}</td>
                    <td className="px-3 py-2">{formatDate(item.date_paiement)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-red-600">{formatNumber(item.solde_a_regler)}</td>
                    <td className="px-3 py-2">{item.certificat || '-'}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(item.utilise)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-blue-600">{formatNumber(item.restant)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(item.vendu)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 font-bold">
                <tr>
                  <td colSpan="7" className="px-3 py-2 text-right">TOTAUX:</td>
                  <td className="px-3 py-2 text-right">{formatNumber(totalAchat)} DH</td>
                  <td className="px-3 py-2 text-right text-green-600">{formatNumber(totalPaye)} DH</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right text-red-600">{formatNumber(totalSolde)} DH</td>
                  <td colSpan="4" className="px-3 py-2"></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Pied de page */}
        <div className="bg-gray-50 px-6 py-3 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {stock.length} ligne(s) • Dernière mise à jour: {new Date().toLocaleString('fr-FR')}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockFournisseurDetaille;
